import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { dehydrate, DehydratedState, QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { Fragment, useEffect, useRef, useState } from 'react';

import { PageHead } from '_components/base/head';
import { LayoutPagesRouter } from '_components/layout/LayoutPagesRouter';
import { PageSeoTag } from '_components/SEO';
import { cacheConfig } from '_config/cache.config';
import { extractDomainRelevantInfoFromHeaders } from '_config/domain.config';
import { AppProviders } from '_core/app-providers';
import { PublicAppContextProvider } from '_core/context/public-app-context';
import { PublicApiRepo } from '_core/repos/public-api.repo';
import { baseSsrPrefetchQueries } from '_core/utils/public-api';
import { getServerSideTranslations } from '_core/utils/ssr';
import { terms_DE, terms_EN, terms_ES, terms_FR, terms_IT, terms_NL } from '_features/legal/data';
import { legalConfig } from '_features/legal/legal.config';
import LegalPage from '_features/legal/legal.page';
import { LegalSection } from '_features/legal/legal.types';

type Prop = {
  locale: string;
  canonical: string;
  origin: string;
  publicApiNavBarUrl: string;
  dehydratedState: DehydratedState;
};

export default function PageRoute(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <AppProviders dehydratedState={props.dehydratedState}>
      <Route {...props} />
    </AppProviders>
  );
}

const Route = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { canonical, origin, locale = 'en', publicApiNavBarUrl } = props;
  const { t } = useTranslation(legalConfig.i18nNamespaces);
  const scrollableCtn = useRef<HTMLDivElement>(null);
  const defaultTerms = terms_EN();
  const lastUpdateDate: Date = new Date('2024-04-18');
  const [pageContent, setPageContent] = useState<LegalSection[]>(defaultTerms);

  PublicApiRepo.publicApiNavBarUrl = publicApiNavBarUrl;

  const handlePageContent = (locale: string) => {
    switch (locale) {
      case 'it':
        return setPageContent(terms_IT());
      case 'fr':
        return setPageContent(terms_FR());
      case 'de':
        return setPageContent(terms_DE());
      case 'es':
        return setPageContent(terms_ES());
      case 'nl':
        return setPageContent(terms_NL());
      default:
        return setPageContent(defaultTerms);
    }
  };

  useEffect(() => {
    handlePageContent(locale);
  }, []);

  return (
    <Fragment>
      <PageHead />
      <PublicAppContextProvider
        canonical={canonical}
        origin={origin}
        locale={locale}
        briefingOptions={{
          page: 'terms',
        }}>
        <LayoutPagesRouter scrollableCtn={scrollableCtn}>
          <PageSeoTag
            title={t('legal:terms.pageTitle')}
            description={t('legal:terms.pageTitle')}
            canonical={canonical}
          />
          <LegalPage
            pageDate={lastUpdateDate}
            pageContent={pageContent}
            pageTitle={t('legal:terms.pageTitle')}
            contentTitle={t('legal:terms.contentTitle')}
            scrollableCtn={scrollableCtn}
            lang={locale}
            origin={origin}
          />
        </LayoutPagesRouter>
      </PublicAppContextProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps<Prop> = async (context) => {
  const { req } = context;
  const { canonical, origin, locale } = extractDomainRelevantInfoFromHeaders(req);

  const queryClient = new QueryClient();
  let publicApiNavBarUrl = '';

  try {
    const response = await baseSsrPrefetchQueries(origin, locale, context.resolvedUrl, queryClient);
    publicApiNavBarUrl = response.publicApiNavBarUrl;
  } catch (e) {
    // Limitation: Typescript exceptions are not typed, so we rely on what is supposed to happen
    if ((e as any).status !== 404) {
      throw e;
    }
  }

  const cacheOptions = cacheConfig.pages['staticPages'];
  if (!context.res.headersSent && cacheOptions != null) {
    context.res.setHeader('Cache-Control', cacheOptions.httpCacheControl);
  }

  const { i18nNamespaces } = legalConfig;
  return {
    props: {
      locale,
      canonical,
      origin,
      publicApiNavBarUrl,
      dehydratedState: dehydrate(queryClient),
      ...(await getServerSideTranslations(locale, i18nNamespaces)),
    },
  };
};
